// src/modules/pos/pages/SaleDetailPage.tsx
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import {
  Button, Typography, Paper, Table, TableHead, TableRow,
  TableCell, TableBody, Divider, Alert, Stack,
} from "@mui/material";

export default function SaleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const auth = token;
  const [sale, setSale] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/sales/${id}`, auth ? { headers: { Authorization: `Bearer ${auth}` } } : undefined as any);
        setSale(res.data);
      } catch (err: any) {
        setError(err?.response?.data?.message || "Could not fetch sale");
      }
    })();
  }, [id, auth]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!sale) return <div>Loading...</div>;

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <Typography variant="h4" gutterBottom>
        Sale Receipt&nbsp;
        {sale.saleNumber || ("#" + (sale._id?.slice(-6)?.toUpperCase() ?? ""))}
      </Typography>
      <Paper className="p-4 mb-4">
        <Typography variant="subtitle1">
          Date: {sale.createdAt ? new Date(sale.createdAt).toLocaleString() : "—"}
        </Typography>
        <Typography variant="subtitle1">
          Cashier: {sale.createdBy?.fullName || sale.createdBy || "—"}
        </Typography>
        {sale.walkInCustomer ? (
          <Typography variant="subtitle1">
            Walk-in Customer: {sale.walkInCustomer?.name || "(Anonymous)"}
          </Typography>
        ) : (
          <Typography variant="subtitle1">
            Customer: {sale.clientId?.fullName || "—"}
          </Typography>
        )}
        {sale.ticketId && (
          <Typography variant="subtitle2" color="primary">
            Linked Ticket: {sale.ticketId}
          </Typography>
        )}
      </Paper>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Item</TableCell>
            <TableCell>Qty</TableCell>
            <TableCell>Price</TableCell>
            <TableCell>Tax</TableCell>
            <TableCell>Discount</TableCell>
            <TableCell align="right">Line Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sale.items.map((item: any, idx: number) => (
            <TableRow key={idx}>
              <TableCell>{item.name}</TableCell>
              <TableCell>{item.quantity}</TableCell>
              <TableCell>${(item.sellingPrice ?? item.unitPrice ?? 0).toFixed(2)}</TableCell>
              <TableCell>
                {typeof item.taxRate === "number" ? `${item.taxRate}%` : "0%"}
              </TableCell>
              <TableCell>
                ${typeof item.discount === "number" ? item.discount.toFixed(2) : "0.00"}
              </TableCell>
              <TableCell align="right">
                ${(item.lineTotal ?? (item.quantity * (item.sellingPrice ?? item.unitPrice ?? 0) * (1 + (item.taxRate || 0) / 100) - (item.discount || 0))).toFixed(2)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Divider className="my-4" />
      <div className="text-right mb-2">
        <Typography>Subtotal: ${Number(sale.subtotal || 0).toFixed(2)}</Typography>
        <Typography>Tax: ${Number(sale.taxTotal || 0).toFixed(2)}</Typography>
        <Typography>Discount: -${Number(sale.discountTotal || 0).toFixed(2)}</Typography>
        <Typography variant="h6">Total: ${Number(sale.total || 0).toFixed(2)}</Typography>
        <Typography>Status: <strong>{(sale.status || "—").toUpperCase()}</strong></Typography>
      </div>

      {/* Payments */}
      {Array.isArray(sale.payments) && sale.payments.length > 0 && (
        <>
          <Divider className="my-4" />
          <Typography variant="subtitle1" gutterBottom>Payments</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Method</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sale.payments.map((pay: any, idx: number) => (
                <TableRow key={idx}>
                  <TableCell>{pay.method}</TableCell>
                  <TableCell>${Number(pay.amount).toFixed(2)}</TableCell>
                  <TableCell>{pay.reference || "—"}</TableCell>
                  <TableCell>
                    {pay.paidAt ? new Date(pay.paidAt).toLocaleString() : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      <Divider className="my-4" />
      <Stack direction="row" spacing={2}>
        <Button variant="outlined" color="primary" onClick={() => window.print()}>
          Print Receipt
        </Button>
        {sale.status !== "voided" && (
          <Button
            variant="outlined"
            color="warning"
            onClick={async () => {
              if (!window.confirm("Are you sure you want to void this sale?")) return;
              try {
                await api.patch(`/pos/${sale._id}/void`, undefined, auth ? { headers: { Authorization: `Bearer ${auth}` } } : undefined as any);
                navigate(0);
              } catch (err: any) {
                setError(err?.response?.data?.message || "Could not void sale");
              }
            }}
          >
            Void Sale
          </Button>
        )}
        {sale.status !== "voided" && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => navigate(`/refunds/new?saleId=${sale._id}`)}
          >
            Process Refund
          </Button>
        )}
      </Stack>
    </div>
  );
}
